import { Dialog, IconButton, Input } from '@nrg/design-system';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LuSearch, LuX } from 'react-icons/lu';
import { useNavigate } from 'react-router';
import type { SearchResult } from '@/modules/topbar/data/useGlobalSearch';
import { useGlobalSearch } from '@/modules/topbar/data/useGlobalSearch';
import { shortcutKey } from '@/modules/topbar/utils/kbd';
import { useSearchStore } from '@/stores';
import styles from './SearchField.module.css';
import { optionDomId, SearchResults } from './SearchResults';

const DEBOUNCE_MS = 150;
const SHORTCUT_KEYS = 'Meta+K';

interface SearchFieldProps {
  variant?: 'inline' | 'iconTrigger';
}

/**
 * Global search: a combobox over the app's own data — destinations, CPMS
 * charging stations and locations, ODA metering points (see `useGlobalSearch`) —
 * that navigates to whatever is picked.
 *
 * Two shapes, one behaviour: inline in the topbar with the results anchored
 * beneath it, or (below `md`) an icon that opens the same field in a dialog.
 * Focus never leaves the input; the highlight travels via
 * `aria-activedescendant`, which is why the options carry no tabindex.
 */
export function SearchField({ variant = 'inline' }: SearchFieldProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setQuery = useSearchStore((s) => s.setQuery);
  const clear = useSearchStore((s) => s.clear);
  const storeQuery = useSearchStore((s) => s.query);

  const [local, setLocal] = useState(storeQuery);
  const inputRef = useRef<HTMLInputElement>(null);
  const dialogInputRef = useRef<HTMLInputElement>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [panelRequested, setPanelRequested] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const listboxId = useId();
  const keyHint = shortcutKey();

  // The store holds the debounced query; the input holds every keystroke. Only
  // the debounced value drives the search, so typing never fires a request per
  // character.
  useEffect(() => {
    const id = setTimeout(() => setQuery(local), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [local, setQuery]);

  const { groups, results, isFetching, isError } = useGlobalSearch(storeQuery);

  const isDialog = variant === 'iconTrigger';
  const hasResults = results.length > 0;
  const panelOpen = isDialog ? dialogOpen : panelRequested && local.trim().length > 0;
  const listboxOpen = panelOpen && hasResults;
  const activeResult = listboxOpen ? results[activeIndex] : undefined;
  const activeOptionId = activeResult ? optionDomId(listboxId, activeResult.id) : undefined;

  // A new result set invalidates the old position — start at the top again.
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the result set identity.
  useEffect(() => {
    setActiveIndex(0);
  }, [results]);

  // Keep the highlight visible while arrowing through a scrolled list.
  useEffect(() => {
    if (!activeOptionId) return;
    document.getElementById(activeOptionId)?.scrollIntoView({ block: 'nearest' });
  }, [activeOptionId]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const isShortcut = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';
      if (!isShortcut) return;
      event.preventDefault();
      if (isDialog) {
        setDialogOpen(true);
      } else {
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isDialog]);

  useEffect(() => {
    if (dialogOpen) {
      // Defer to let the Dialog render its content before focusing.
      const id = setTimeout(() => dialogInputRef.current?.focus(), 0);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [dialogOpen]);

  const reset = useCallback(() => {
    setLocal('');
    clear();
    setPanelRequested(false);
  }, [clear]);

  const selectResult = useCallback(
    (result: SearchResult) => {
      navigate(result.to);
      reset();
      setDialogOpen(false);
    },
    [navigate, reset],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape') {
        // Clearing takes precedence over closing: an emptied field is the state
        // people expect after Escape, and the panel closes with it.
        if (local) {
          event.preventDefault();
          reset();
        } else {
          setPanelRequested(false);
        }
        return;
      }

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (!hasResults) return;
        event.preventDefault();
        setPanelRequested(true);
        const delta = event.key === 'ArrowDown' ? 1 : -1;
        setActiveIndex((index) => (index + delta + results.length) % results.length);
        return;
      }

      if (event.key === 'Enter' && activeResult) {
        event.preventDefault();
        selectResult(activeResult);
      }
    },
    [activeResult, hasResults, local, reset, results.length, selectResult],
  );

  const highlightResult = useCallback(
    (result: SearchResult) => {
      const index = results.indexOf(result);
      if (index >= 0) setActiveIndex(index);
    },
    [results],
  );

  const shortcutHint = (
    <kbd className={styles.shortcut}>{t('topbar.search.shortcutHint', { key: keyHint })}</kbd>
  );

  const clearButton = (focusRef: React.RefObject<HTMLInputElement | null>) => (
    <button
      type="button"
      aria-label={t('topbar.search.clearLabel')}
      onClick={() => {
        reset();
        focusRef.current?.focus();
      }}
    >
      <LuX aria-hidden="true" />
    </button>
  );

  const inputProps = useMemo(
    () => ({
      type: 'search' as const,
      role: 'combobox',
      'aria-expanded': listboxOpen,
      'aria-autocomplete': 'list' as const,
      'aria-label': t('topbar.search.label'),
      'aria-keyshortcuts': SHORTCUT_KEYS,
      ...(listboxOpen ? { 'aria-controls': listboxId } : {}),
      ...(activeOptionId ? { 'aria-activedescendant': activeOptionId } : {}),
      placeholder: t('topbar.search.placeholder'),
      value: local,
      onKeyDown: handleKeyDown,
    }),
    [activeOptionId, handleKeyDown, listboxId, listboxOpen, local, t],
  );

  const resultList = (
    <SearchResults
      listboxId={listboxId}
      activeOptionId={activeOptionId}
      groups={groups}
      query={local}
      isFetching={isFetching}
      isError={isError}
      onSelect={selectResult}
      onHighlight={highlightResult}
    />
  );

  if (isDialog) {
    return (
      <>
        <IconButton
          label={t('topbar.search.openLabel')}
          variant="secondary"
          onClick={() => setDialogOpen(true)}
        >
          <LuSearch aria-hidden="true" />
        </IconButton>
        <Dialog.Root open={dialogOpen} onOpenChange={(detail) => setDialogOpen(detail.open)}>
          <Dialog.Portal>
            <Dialog.Backdrop />
            <Dialog.Positioner placement="top">
              <Dialog.Content size="md" placement="top">
                <Dialog.Header>
                  <Dialog.Title>{t('topbar.search.dialogTitle')}</Dialog.Title>
                </Dialog.Header>
                <Dialog.Body>
                  <Input
                    {...inputProps}
                    ref={dialogInputRef}
                    className={styles.dialogField}
                    size="md"
                    variant="subtle"
                    onChange={(event) => setLocal(event.target.value)}
                    iconLeft={<LuSearch aria-hidden="true" />}
                    iconRight={local ? clearButton(dialogInputRef) : shortcutHint}
                  />
                  <div className={styles.dialogResults}>{resultList}</div>
                </Dialog.Body>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Portal>
        </Dialog.Root>
      </>
    );
  }

  return (
    <div className={styles.wrapper}>
      <Input
        {...inputProps}
        ref={inputRef}
        className={styles.field}
        size="sm"
        variant="subtle"
        onChange={(event) => {
          setLocal(event.target.value);
          setPanelRequested(event.target.value.trim().length > 0);
        }}
        onFocus={() => setPanelRequested(local.trim().length > 0)}
        // A click on a result is caught by its `onMouseDown` preventDefault, so
        // blur here only fires when focus genuinely leaves the field.
        onBlur={() => setPanelRequested(false)}
        iconLeft={<LuSearch aria-hidden="true" />}
        iconRight={local ? clearButton(inputRef) : shortcutHint}
      />
      {panelOpen && <div className={styles.panel}>{resultList}</div>}
    </div>
  );
}
